import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, HardHat, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ResetPassword() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const token = search.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!token) {
    return (
      <CenteredCard>
        <h1 className="font-display text-2xl font-black uppercase tracking-tight">Invalid link</h1>
        <p className="text-sm text-zinc-600 mt-3">This reset link is missing its token. Request a fresh one.</p>
        <Link to="/forgot-password" className="block mt-6">
          <Button className="w-full h-11 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider">Request reset</Button>
        </Link>
      </CenteredCard>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new one.");
      nav("/login");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not reset password. Request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <CenteredCard>
      <form onSubmit={submit} data-testid="reset-password-form">
        <div className="label-tag">/ new password</div>
        <h1 className="font-display text-2xl font-black uppercase tracking-tight mt-2">Set a new password</h1>
        <p className="text-sm text-zinc-600 mt-2">Pick something memorable. Minimum 8 characters.</p>
        <div className="mt-6">
          <Label className="label-tag">New password</Label>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input type="password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pl-9 rounded-sm border-zinc-400" placeholder="••••••••" data-testid="reset-password-input" />
          </div>
        </div>
        <div className="mt-4">
          <Label className="label-tag">Confirm password</Label>
          <div className="relative mt-2">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 pl-9 rounded-sm border-zinc-400" placeholder="••••••••" data-testid="reset-confirm-input" />
          </div>
        </div>
        {err && <div className="mt-4 border border-red-300 bg-red-50 text-red-700 p-3 text-sm" data-testid="reset-error">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase font-bold tracking-wider mt-6" data-testid="reset-submit-button">
          {busy ? "Saving…" : <>Save new password <ArrowRight className="ml-2 w-4 h-4" /></>}
        </Button>
        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-zinc-600 hover:text-[#FF5F15] hover:underline">Back to sign in</Link>
        </div>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F4F4F5]">
      <div className="w-full max-w-md bg-white border-4 border-[#09090B]">
        <div className="bg-[#09090B] p-4 flex items-center gap-2 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#FF5F15] flex items-center justify-center">
              <HardHat className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-black">FIELDLINK<span className="text-[#FF5F15]">.CO.NZ</span></span>
          </Link>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}
